import './pill-bar.css';

import { Util } from '../../../utils/util';
import { IPillBarData, IPillBarDataButton } from './pill-bar.interface';
import { ArrowScroller } from '../ArrowScroller/arrow-scroller';
import { LocalizationService } from '../../../services/localization-service';

/**
 * Represents a pill-style tab bar.
 */
export class PillBar {
    private readonly _item: IPillBarData;
    private readonly _containerElement: HTMLElement;
    private readonly _arrowScroller: ArrowScroller;

    /**
     * Initializes a new instance of the PillBar.
     * @param {IPillBarData} item - The data for the pill bar buttons.
     * @param {LocalizationService} [localizationService] - Localizes the scroll arrows' labels.
     */
    constructor(item: IPillBarData, localizationService?: LocalizationService) {
        this._item = item;
        const { element, selectedId } = this._createElement();
        this._containerElement = element;

        this._arrowScroller = new ArrowScroller(this._containerElement, { localizationService });

        if (selectedId)
            this._setActiveTab(selectedId);
    }

    /**
     * Gets the main container element of the PillBar.
     * @returns {HTMLElement} The pill bar container element.
     */
    public get element(): HTMLElement {
        return this._arrowScroller.element;
    }

    /**
     * Updates the visibility and positioning of the pill bar's left/right scroll arrows
     * based on the current scroll state.
     * Typically called after rendering or window resize.
     */
    public updateBeginningArrows(): void {
        this._arrowScroller.update();
    }

    /**
     * Clears the active state from all tabs.
     */
    public clear(): void {
        const listItems = Array.from(this.element.querySelectorAll("li"));
        for (const item of listItems) {
            item.classList.remove("active");
            item.ariaSelected = "false";
        }
    }

    /**
     * Creates the root HTML element for the pill bar and appends all tab items.
     * @private
     * @returns {{ element: HTMLElement, selectedId: string }} An object containing the root element and the ID of the selected tab (if any).
     */
    private _createElement(): { element: HTMLElement, selectedId: string } {
        const html = `<ul id="ul_${this._item.buttonId}" class="pill-bar-container" role="tablist"></ul>`

        const element = Util.createHtmlElementFromTemplate(html);

        if (this._item.ariaLabelledBy)
            element.setAttribute("aria-labelledby", this._item.ariaLabelledBy);

        let selectedId: string = "";
        for (const tab of this._item.buttons) {
            const list = this._createListItem(tab);
            element.appendChild(list);

            if (tab.selected)
                selectedId = list.id;
        }

        return { element, selectedId };
    }

    /**
     * Creates a single list item (`<li>`) element for a tab button in the pill bar.
     * Sets up attributes, localization tags, and click handling.
     * 
     * @private
     * @param {IPillBarDataButton} data - The data for the individual tab item.
     * @returns {HTMLElement} The created `<li>` element.
     */
    private _createListItem(data: IPillBarDataButton): HTMLElement {
        const id = `li_${this._item.buttonId}_${data.id}`;

        const html = `<li id="${id}">${data.text}</li>`;
        const element = Util.createHtmlElementFromTemplate(html) as HTMLLIElement;
        element.tabIndex = 0;
        element.setAttribute("role", "tab");

        if (data.disabled) {
            element.style.pointerEvents = "none";
            element.style.opacity = "0.5";
            element.setAttribute("aria-disabled", "true");
            element.tabIndex = -1;
        }

        if (data.ltag)
            element.setAttribute("ltag", data.ltag);

        if (data.ltagj)
            element.setAttribute("ltagj", Util.toLtagj(data.ltagj));

        if (data.ariaDescribedBy)
            element.setAttribute("aria-describedby", data.ariaDescribedBy);

        if (data.ariaControls)
            element.setAttribute("aria-controls", data.ariaControls);

        element.onclick = (event: MouseEvent) => {
            element.dataset.humanClick = String(event.isTrusted);

            const result = data.function();
            if (result === true || result === undefined)
                this._setActiveTab(id);
        };

        element.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                event.preventDefault();
                const previousTab = element.previousElementSibling as HTMLLIElement | null;
                previousTab?.focus();
            } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                event.preventDefault();
                const nextTab = element.nextElementSibling as HTMLLIElement | null;
                nextTab?.focus();
            }
        });

        element.addEventListener("focus", () => this._scrollTabIntoView(element));

        Util.addKeyboardActivation(element);

        return element;
    }

    private _scrollTabIntoView(tab: HTMLLIElement | null): void {
        tab?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }

    /**
     * Sets the active tab by its ID.
     * @param {string} id - The ID of the tab to activate.
     */
    private readonly _setActiveTab = (id: string): void => {
        this.clear();

        const button = this.element.querySelector(`#${id}`) as HTMLLIElement | undefined;
        if (button) {
            this._activateButton(button);
        } else {
            const firstButton = this.element.querySelector("li") as HTMLLIElement | undefined;;
            if (firstButton)
                this._activateButton(firstButton);
        }
    }

    /**
    * Activates a tab button.
    * @param {HTMLLIElement} list - The button to activate.
    */
    private _activateButton(list: HTMLLIElement): void {
        list.classList.add("active");
        list.ariaSelected = "true";
        list.tabIndex = 0;
        list.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
}